import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, formatPrice } from "@/lib/api";
import { useSettings, whatsappLink } from "@/context/SettingsContext";
import { Loader } from "@/components/States";
import { CheckCircle2, Copy, MessageCircle } from "lucide-react";
import { toast } from "sonner";

export default function OrderSuccess() {
  const { orderId } = useParams();
  const { settings } = useSettings();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qrCode, setQrCode] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/orders/track/${orderId}`)
      .then((r) => setOrder(r.data))
      .finally(() => setLoading(false));
    axios.get(`${API}/settings/qr`).then((r) => setQrCode(r.data.qr_code || "")).catch(() => {});
  }, [orderId]);

  if (loading) return <Loader />;

  const waMsg = order
    ? `Hi ${settings.business_name || "Taanya Homefoods"}, I just placed an order.\n\n*Order ID:* ${order.order_id}\n${order.items
        .map((i) => `• ${i.name} (${i.variant_label}) ×${i.quantity}${i.customization ? ` — "${i.customization}"` : ""}`)
        .join("\n")}\n\n*Total:* ${formatPrice(order.total)}\n\nName: ${order.customer.name}\nAddress: ${order.customer.address}, ${order.customer.city} - ${order.customer.pincode}`
    : "";

  return (
    <div className="mx-auto max-w-2xl animate-fade-in px-4 py-16 sm:px-6 lg:px-8">
      <div className="rounded-3xl border border-brand-line bg-white p-8 text-center">
        <CheckCircle2 className="mx-auto h-16 w-16 text-brand-green" />
        <h1 className="mt-4 font-heading text-4xl text-brand-dark">Order Placed!</h1>
        <p className="mt-2 text-brand-muted">Thank you for choosing {settings.business_name || "Taanya Homefoods"}.</p>

        <div className="mt-6 rounded-2xl bg-brand-gold-light/50 p-5">
          <p className="text-xs uppercase tracking-widest text-brand-muted">Your Order ID</p>
          <div className="mt-1 flex items-center justify-center gap-2">
            <span data-testid="order-id" className="font-heading text-3xl text-brand">
              {order?.order_id}
            </span>
            <button
              data-testid="copy-order-id"
              onClick={() => {
                navigator.clipboard.writeText(order.order_id);
                toast.success("Order ID copied");
              }}
              className="rounded-full p-2 text-brand-muted hover:bg-white"
            >
              <Copy className="h-4 w-4" />
            </button>
          </div>
          <p className="mt-2 text-sm text-brand-muted">Save this ID to track your order status.</p>
        </div>

        {settings.upi_id && (
          <div className="mt-6 rounded-2xl border border-brand-line p-5 text-left">
            <h3 className="font-heading text-xl text-brand-dark">Payment</h3>
            <p className="mt-1 text-sm text-brand-muted">{settings.payment_instructions}</p>
            <p className="mt-2 text-sm">
              <span className="text-brand-muted">UPI ID: </span>
              <span className="font-semibold text-brand-dark">{settings.upi_id}</span>
            </p>
            {order?.utr && (
              <p className="mt-2 text-sm">
                <span className="text-brand-muted">UTR: </span>
                <span className="font-semibold text-brand-dark">{order.utr}</span>
              </p>
            )}
            <p className="mt-2 text-sm">
              <span className="text-brand-muted">Payment status: </span>
              <span data-testid="success-payment-status" className="font-semibold text-brand-dark">{order?.payment_status || "Payment Pending"}</span>
            </p>
            <p className="mt-1 text-xs text-brand-muted">We'll manually verify your payment and update the status.</p>
            {qrCode && <img src={qrCode} alt="Payment QR" className="mt-3 h-40 w-40 rounded-xl object-contain" />}
          </div>
        )}

        <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
          <a
            data-testid="success-whatsapp-btn"
            href={whatsappLink(settings.whatsapp, waMsg)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3 font-medium text-white transition-transform hover:-translate-y-0.5"
          >
            <MessageCircle className="h-5 w-5" /> Confirm on WhatsApp
          </a>
          <Link
            to={`/track?id=${order?.order_id}`}
            data-testid="success-track-btn"
            className="rounded-full border-2 border-brand-gold px-6 py-3 font-medium text-brand-green transition-colors hover:bg-brand-gold-light"
          >
            Track Order
          </Link>
        </div>
        <Link to="/shop" className="mt-4 inline-block text-sm text-brand-muted hover:text-brand">
          Continue shopping
        </Link>
      </div>
    </div>
  );
}
